import { useMemo, useState } from "react";
import { MapPin, Pencil, Plus, Search, Trash2 } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { tipoSitioEnum, TIPO_SITIO_LABEL, type TipoSitio, type Sitio } from "./schemas";
import { useDeleteSitio, useListSitios, useSitio } from "./api";
import { SitioFormDialog } from "./SitioFormDialog";
// rc140 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

export function SitiosListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [tipo, setTipo] = useState<TipoSitio | "">("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: editing } = useSitio(editingId);

  const filters = useMemo(() => ({ page, size: 20, search, tipo }), [page, search, tipo]);
  const { data, isLoading, isFetching } = useListSitios(filters);
  const deleteMut = useDeleteSitio();
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleDelete(s: Sitio) {
    if (!confirm(`¿Eliminar el sitio "${s.nombre}"?`)) return;
    try { await deleteMut.mutateAsync(s.id); }
    catch (e) { alert(`Error: ${(e as { message?: string })?.message}`); }
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Sitios</h2>
          <p className="text-xs text-muted-foreground">{data ? `${data.total} ${data.total === 1 ? "sitio" : "sitios"}` : "Cargando…"}</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> <span className="max-md:hidden">Nuevo sitio</span><span className="md:hidden">Nuevo</span></Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 max-md:[&>select]:flex-1 max-md:[&>select]:w-auto">
        <div className="relative flex-1 min-w-[220px] max-md:basis-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} placeholder="Buscar por nombre…" className="pl-8" />
        </div>
        <Select value={tipo} onChange={(e) => { setTipo(e.target.value as TipoSitio | ""); setPage(1); }}>
          <option value="">Todos los tipos</option>
          {tipoSitioEnum.map((t) => <option key={t} value={t}>{TIPO_SITIO_LABEL[t]}</option>)}
        </Select>
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>{search || tipo ? "Sin coincidencias." : "Registra el primer sitio."}</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((s) => {
              const loc = [s.municipio, s.estado].filter(Boolean).join(", ");
              return (
                <MobileCard
                  key={s.id}
                  onClick={() => setEditingId(s.id)}
                  title={s.nombre}
                  subtitle={loc || "—"}
                  right={<Badge variant={s.activo ? "success" : "secondary"}>{s.activo ? "activo" : "inactivo"}</Badge>}
                  actions={
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(s.id)}><Pencil size={13} /> Editar</Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(s)}><Trash2 size={13} /> Eliminar</Button>
                    </>
                  }
                >
                  <MobileMeta label="Tipo">{TIPO_SITIO_LABEL[s.tipo]}</MobileMeta>
                  <MobileMeta label="Altura">{s.altura_metros ? `${s.altura_metros} m` : "—"}</MobileMeta>
                </MobileCard>
              );
            })}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Ubicación</TableHead>
              <TableHead>Altura</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState icon={MapPin} title="Sin sitios" description={search || tipo ? "Sin coincidencias." : "Registra el primer sitio."}
                  action={!search && !tipo ? <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> Nuevo sitio</Button> : undefined} />
              </TableEmpty>
            ) : (
              items.map((s) => {
                const loc = [s.municipio, s.estado].filter(Boolean).join(", ");
                return (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => setEditingId(s.id)}>
                    <TableCell className="font-medium">{s.nombre}</TableCell>
                    <TableCell><Badge variant="outline">{TIPO_SITIO_LABEL[s.tipo]}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{loc || "—"}</TableCell>
                    <TableCell>{s.altura_metros ? `${s.altura_metros} m` : "—"}</TableCell>
                    <TableCell><Badge variant={s.activo ? "success" : "secondary"}>{s.activo ? "activo" : "inactivo"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => setEditingId(s.id)} title="Editar"><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(s)} title="Eliminar"><Trash2 size={14} className="text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">Página {data?.page ?? 1} de {totalPages}{isFetching && " · actualizando…"}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        )}
      </div>
      )}

      <SitioFormDialog open={createOpen} onOpenChange={setCreateOpen} sitio={null} />
      <SitioFormDialog open={!!editingId && !!editing} onOpenChange={(v) => { if (!v) setEditingId(null); }} sitio={editing ?? null} />
    </div>
  );
}
